import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { Chrome } from "./components/Chrome.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { Notices } from "./components/Notices.tsx";
import { Unreachable } from "./components/Unreachable.tsx";
import { SessionScreen } from "./routes/SessionScreen.tsx";
import { ExerciseScreen } from "./routes/ExerciseScreen.tsx";
import { PickSession } from "./routes/PickSession.tsx";
import { Home } from "./routes/Home.tsx";
import { SIGN_IN_PATH } from "./lib/signin.ts";
import { screenOf } from "./lib/screen.ts";

/**
 * The two in-gym screens ship in the main bundle so they open instantly on a
 * phone with one bar of signal. The charting library only loads when the
 * progression view is actually opened.
 */
const Progress = lazy(() => import("./routes/Progress.tsx").then((m) => ({ default: m.Progress })));
const History = lazy(() => import("./routes/History.tsx").then((m) => ({ default: m.History })));
const Settings = lazy(() => import("./routes/Settings.tsx").then((m) => ({ default: m.Settings })));
const SettingsExercises = lazy(() =>
  import("./routes/SettingsExercises.tsx").then((m) => ({ default: m.SettingsExercises })),
);
const SettingsAccount = lazy(() =>
  import("./routes/SettingsAccount.tsx").then((m) => ({ default: m.SettingsAccount })),
);
const AddExercise = lazy(() => import("./routes/AddExercise.tsx").then((m) => ({ default: m.AddExercise })));
const SignIn = lazy(() => import("./routes/SignIn.tsx").then((m) => ({ default: m.SignIn })));

export function App() {
  const { pathname } = useLocation();

  // Outside the chrome: its header asks the server for today's session, which
  // is exactly what a signed-out device cannot do.
  if (pathname === SIGN_IN_PATH) {
    return (
      <>
        <Notices />
        <Suspense fallback={<div className="page py-20 text-ink-mute">Chargement…</div>}>
          <SignIn />
        </Suspense>
      </>
    );
  }

  // A new boundary per screen, so a crash does not outlive the page it
  // happened on. The exercises and the one open beside them are one screen:
  // remounting it on every pick sent the list back to its top.
  const screen = screenOf(pathname);

  return (
    <Chrome>
      <Notices />
      <Unreachable />
      <ErrorBoundary key={screen}>
        <Suspense fallback={<div className="page py-20 text-ink-mute">Chargement…</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/choisir" element={<PickSession />} />
            <Route path="/seance/:id" element={<SessionScreen />} />
            <Route path="/seance/:id/exercice/:index" element={<ExerciseScreen />} />
            <Route path="/seance/:id/ajouter" element={<AddExercise />} />
            <Route path="/progression" element={<Progress />} />
            <Route path="/historique" element={<History />} />
            <Route path="/reglages" element={<Settings />} />
            <Route path="/reglages/exercices/:id?" element={<SettingsExercises />} />
            {/* The sessions done moved to the journal; an old link still lands there. */}
            <Route path="/reglages/seances" element={<Navigate to="/historique" replace />} />
            <Route path="/reglages/compte" element={<SettingsAccount />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Chrome>
  );
}
